import { ActionType } from '@/actions/actiontypes';
import SimpleSelect from '@/components/objects/SimpleSelect';
import SimpleToggle from '@/components/objects/SimpleToggle';
import { ExportEnumFileType } from '@/domain/ExportEnumFileType';
import { ExportEnumFormat, MapExportEnumFormatToDisplayName } from '@/domain/ExportEnumFormat';
import { MapProjeksjonToDisplayName, Projeksjon } from '@/domain/Projeksjon';
import { stateOmrader, statePreferences } from '@/selectors/selectors';
import { ApiVersion, PreferencesState, VegobjekttypeCategoryState, VegobjekttypeState } from '@/state';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { useDispatch, useSelector } from '@/state/store';
import { handleDownload, useIsSensitiveAvailable, useUrlsParams } from '@/utils/exportUtils';
import { kartutsnittFromWktStrings } from '@/utils/SpatialHelper';
import { getApiVersion } from '@/utils/Utils';
import { SVVButtonLink } from '@komponentkassen/svv-button';
import { SVVModal } from '@komponentkassen/svv-modal';
import { SVVTab, SVVTabContent, SVVTabList, SVVTabs } from '@komponentkassen/svv-tabs';
import * as React from 'react';
import { useContext, useEffect } from 'react';

interface ExportDialogProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    setDownloading: (downloading: boolean) => void;
    downloadUrl: string;
    setDownloadUrl: (url: string) => void;
    votState: VegobjekttypeState;
    catState?: VegobjekttypeCategoryState;
}

type KeyOfType<T, V> = keyof {
    [P in keyof T as T[P] extends V ? P : never]: any;
};

type StringPreferenceKeys = KeyOfType<PreferencesState, string>;
type BooleanPreferenceKeys = KeyOfType<PreferencesState, boolean>;

interface SelectProps {
    name: StringPreferenceKeys;
    label: string;
    options: string[];
    optionsDisplayMap?: Map<string, string>;
}

interface ToggleProps {
    name: BooleanPreferenceKeys;
    label: string;
}

export const ExportDialog = (exportDialogProps: ExportDialogProps) => {
    const preferences = useSelector(statePreferences);
    const omraderState = useSelector(stateOmrader);
    const dispatch = useDispatch();
    const context = useContext(ConfigAndDataContext);

    const isSensitiveAvailable = useIsSensitiveAvailable(exportDialogProps.votState.typeId);
    const urlsParams = useUrlsParams(exportDialogProps.votState, exportDialogProps.catState);

    const csvLink = () => {
        const { restapi_url_v3, restapi_url, eksport_url } = context.config;
        const apiUrl = getApiVersion() === ApiVersion.V3 ? restapi_url_v3 : restapi_url;
        const { typeId } = exportDialogProps.votState;
        const endpoint = `/vegobjekter/${typeId}.csv?`;
        const urlParams = new URLSearchParams(urlsParams);
        urlParams.append('api', apiUrl);
        urlParams.append('opprettet_dato', preferences.exportOpprettetDato.toString());
        return eksport_url + endpoint + urlParams.toString();
    };

    const sosiLink = () => {
        const { restapi_url_v3, restapi_url, eksport_url } = context.config;
        const apiUrl = getApiVersion() === ApiVersion.V3 ? restapi_url_v3 : restapi_url;

        const { typeId } = exportDialogProps.votState;
        const endpoint = `/vegobjekter/${typeId}.sos?`;
        const urlParams = new URLSearchParams(urlsParams);
        if (urlParams.has('kartutsnitt') && !omraderState.loading) {
            const sosiUtsnitt = kartutsnittFromWktStrings(
                omraderState
                    .withVegsystemreferanser([])
                    .getFullList()
                    .map(omrade => omrade?.vegobjekt?.geometri?.wkt)
                    .filter(e => !!e)
            ).toQueryString();
            urlParams.append('sosiutsnitt', sosiUtsnitt);
        }
        urlParams.append('api', apiUrl);
        urlParams.append('tegnsett', 'utf-8');
        urlParams.append('api', apiUrl);
        preferences.exportEnumFormat && urlParams.append('enumFormat', preferences.exportEnumFormat);
        return eksport_url + endpoint + urlParams.toString();
    };

    useEffect(() => {
        exportDialogProps.setDownloadUrl(
            preferences.exportEnumFileType === ExportEnumFileType.sosi ? sosiLink() : csvLink()
        );
    }, [preferences, exportDialogProps]);

    const handleSubmit = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        handleDownload(
            e,
            isSensitiveAvailable,
            exportDialogProps.downloadUrl,
            context,
            dispatch,
            exportDialogProps.setDownloading
        );
    };

    const handleSelect = (name: StringPreferenceKeys, event: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch({
            type: ActionType.PREFERENCES_UPDATE,
            [name]: event.target.value,
        });
    };

    const handleToggle = (name: BooleanPreferenceKeys, previousValue: boolean) => {
        dispatch({
            type: ActionType.PREFERENCES_UPDATE,
            [name]: !previousValue,
        });
    };

    const Select = ({ name, label, options, optionsDisplayMap }: SelectProps) => (
        <SimpleSelect
            label={label}
            options={options}
            optionLabel={optionsDisplayMap ? option => optionsDisplayMap.get(option) : undefined}
            value={preferences[name]}
            onChange={e => handleSelect(name, e)}
        />
    );

    const Toggle = ({ name, label }: ToggleProps) => (
        <SimpleToggle
            className="c-preferences-toggle"
            checked={preferences[name]}
            label={label}
            onChange={() => handleToggle('exportOpprettetDato', preferences[name])}
        />
    );

    const options = [
        {
            value: ExportEnumFileType.csv,
            label: 'Last ned som CSV' + (isSensitiveAvailable ? ' med sensitive data' : ''),
            content: (
                <div>
                    <Select
                        name="exportProjection"
                        label="Velg kartprojeksjon"
                        options={Object.values(Projeksjon)}
                        optionsDisplayMap={MapProjeksjonToDisplayName}
                    />
                    <Toggle name="exportOpprettetDato" label="Inkluder opprettet dato" />
                </div>
            ),
        },
        {
            value: ExportEnumFileType.sosi,
            label: 'Last ned som SOSI' + (isSensitiveAvailable ? ' med sensitive data' : ''),
            content: (
                <div>
                    <Select
                        name="exportProjection"
                        label="Velg kartprojeksjon"
                        options={Object.values(Projeksjon)}
                        optionsDisplayMap={MapProjeksjonToDisplayName}
                    />
                    <Select
                        name="exportEnumFormat"
                        label="Velg format for tillatte verdier (enum) på SOSI-fil"
                        options={Object.values(ExportEnumFormat)}
                        optionsDisplayMap={MapExportEnumFormatToDisplayName}
                    />
                </div>
            ),
        },
    ];

    return (
        <>
            <SVVModal
                show={exportDialogProps.isOpen}
                onClose={() => exportDialogProps.setIsOpen(false)}
                title={'Eksporter datasett'}
                acceptButton={
                    <SVVButtonLink
                        className="c-results-summary-export-links__download svv-knapp"
                        onClick={e => handleSubmit(e)}
                        href={exportDialogProps.downloadUrl}
                    >
                        Last ned
                    </SVVButtonLink>
                }
            >
                <div className="c-preferences-toggle-content">
                    <SVVTabs
                        defaultValue={preferences.exportEnumFileType}
                        onChange={val => handleSelect('exportEnumFileType', { target: { value: val } })}
                    >
                        <SVVTabList>
                            {options.map(o => (
                                <SVVTab value={o.value} label={o.label} key={o.value} />
                            ))}
                        </SVVTabList>
                        {options.map(o => (
                            <SVVTabContent key={`${o.value}-content`} value={o.value}>
                                {o.content}
                            </SVVTabContent>
                        ))}
                    </SVVTabs>
                </div>
            </SVVModal>
        </>
    );
};

export default ExportDialog;
